import type { NavSection } from "@skypointcloud/skypoint-ui";
import { useMemo } from "react";
import { usePermissions } from "./use-permissions";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { APP_ROUTES, featureAllows } from "@/lib/routes";

/**
 * The nav drawer's visible sections (UX-3), resolved from the one route table.
 *
 * A section with no visible items is omitted entirely, its header included — an
 * empty "Admin" heading tells a Viewer there is something they cannot see,
 * which is noise, not information.
 *
 * Item id is the route's path, so SkyNavDrawer's `selectedId` match is a plain
 * route comparison (`navSelectionFor`) and ids stay unique across sections. The
 * href is the same path, except for an entry with tabs of its own (Settings,
 * round 3: a route with `navUnder` has no entry and is one of those tabs): it
 * opens the first tab the reader may open, in table order, the entry's own
 * route last.
 */
export function useNavSections(): NavSection[] {
  const { canAny } = usePermissions();
  const { me } = useTenantInstance();
  const features = me?.features;

  return useMemo(() => {
    const visible = APP_ROUTES.filter(
      (r) => featureAllows(r, features) && (!r.anyPermission || canAny(...r.anyPermission)),
    );
    // A tab (`navUnder`) or a merged screen (`mergedInto`, round 5) has no entry of its own.
    // A personal page (round 8, "Connected apps") is reached from the user menu, and a link-only
    // page (release 8B, the MCP approve link) from the link alone.
    const entries = visible.filter(
      (r) => !r.navUnder && !r.mergedInto && !r.personal && !r.linkOnly,
    );
    const primary = entries.filter((r) => !r.section);
    const admin = entries.filter((r) => r.section === "Admin");

    const hrefOf = (entry: (typeof visible)[number]) =>
      visible.find((r) => r.navUnder === entry.path)?.path ?? entry.path;

    const toItems = (routes: typeof visible) =>
      routes.map((r) => ({ id: r.path, label: r.title, icon: r.icon, href: hrefOf(r) }));

    return [
      ...(primary.length > 0 ? [{ items: toItems(primary) }] : []),
      ...(admin.length > 0 ? [{ header: "Admin", items: toItems(admin) }] : []),
    ];
  }, [canAny, features]);
}
